import React, { useEffect, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useDispatch } from 'react-redux';
import { getFastParticipantsForPanel, getPanel, ThunkDispatch } from 'store';
import { CsvImportBanner } from './CsvImportBanner';
import { CsvImport, useCsvImport, usePanelCsvImports } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  csvImportId?: string;
  panelId?: string;
}

export const CsvImportBannerList = ({ csvImportId, panelId }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const dispatch = useDispatch<ThunkDispatch>();

  const [interval, setRefreshInterval] = React.useState<number | undefined>(
    undefined,
  );
  const { data: importingCsv } = useCsvImport(csvImportId, {
    refetchInterval: interval,
  });
  const { data: panelCsvImports = [] } = usePanelCsvImports(csvImportId, {
    refetchInterval: interval,
  });

  const csvImports = useMemo(
    () => [importingCsv, ...panelCsvImports].filter(Boolean) as CsvImport[],
    [importingCsv, panelCsvImports],
  );

  useEffect(() => {
    const someInProgress = csvImports.some(
      (csvImport) =>
        csvImport.status !== 'SUCCESS' && csvImport.status !== 'FAILED',
    );
    if (!!someInProgress === !!interval) {
      return;
    }
    if (someInProgress && !interval) {
      setRefreshInterval(300);
      return;
    }

    setRefreshInterval(undefined);

    if (panelId) {
      dispatch(getPanel(panelId));
      dispatch(getFastParticipantsForPanel(panelId));
    }
  }, [csvImports, dispatch, panelId, interval, setRefreshInterval]);

  if (!csvImports.length) {
    return null;
  }

  return (
    <div className={classes.container}>
      {csvImports.map((csvImport) => (
        <CsvImportBanner csvImport={csvImport} key={csvImport.id} />
      ))}
    </div>
  );
};
